import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Mail, Phone, Clock, AlertCircle } from 'lucide-react';
import Seo from '@/components/Seo';

const GrievancePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    category: '',
    subject: '',
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.category || !formData.subject || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    // Simulate submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    toast.success('Your grievance has been submitted. We will contact you within 48 hours.');
    setFormData({ name: '', email: '', phone: '', category: '', subject: '', description: '' });
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16">
      <Seo title="Grievance Redressal" description="Lodge a grievance or complaint with Bharat Estate. Our team responds within 7 working days." path="/grievance" />
      <div className="container-luxury max-w-5xl">
        <div className="bg-white p-8 md:p-12 rounded-sm shadow-sm border border-border/50">
          <h1 className="font-['Plus_Jakarta_Sans'] text-4xl font-bold text-[#0F172A] mb-4">Grievance Redressal</h1>
          <p className="text-muted-foreground mb-8">
            We are committed to addressing your concerns promptly and fairly. Please use this page to submit any grievances or complaints.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Info */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-[#F9F9F7] p-6 rounded-sm">
                <h3 className="font-semibold text-[#0F172A] mb-4">Grievance Officer</h3>
                <p className="text-muted-foreground text-sm mb-2">
                  In accordance with the Information Technology Act, 2000 and rules made thereunder, the name and contact details of the Grievance Officer are provided below:
                </p>
                <div className="mt-4 space-y-2">
                  <p className="text-[#0F172A] font-medium">Mr. Rajesh Kumar</p>
                  <p className="text-muted-foreground text-sm">Grievance Officer</p>
                  <p className="text-muted-foreground text-sm flex items-center gap-2">
                    <Mail className="h-4 w-4" /> contact@bharat.estate
                  </p>
                  <p className="text-muted-foreground text-sm flex items-center gap-2">
                    <Phone className="h-4 w-4" /> +91 94650 24516
                  </p>
                </div>
              </div>

              <div className="bg-[#F9F9F7] p-6 rounded-sm">
                <h3 className="font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5" /> Resolution Timeline
                </h3>
                <ul className="text-muted-foreground text-sm space-y-3">
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-[#0F172A] min-w-[80px]">24 hours:</span>
                    <span>Acknowledgment of grievance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-[#0F172A] min-w-[80px]">48 hours:</span>
                    <span>Initial response with action plan</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-[#0F172A] min-w-[80px]">7 days:</span>
                    <span>Resolution for standard issues</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-[#0F172A] min-w-[80px]">30 days:</span>
                    <span>Resolution for complex issues</span>
                  </li>
                </ul>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-500 p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[#0F172A] font-medium text-sm">Important Note</p>
                    <p className="text-muted-foreground text-sm mt-1">
                      For payment or refund-related grievances, please include your transaction ID and payment receipt.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[#F9F9F7] p-6 rounded-sm">
                <h3 className="font-semibold text-[#0F172A] mb-4">Working Hours</h3>
                <p className="text-muted-foreground text-sm flex items-start gap-2">
                  <Clock className="h-4 w-4 flex-shrink-0 mt-1" />
                  <span>
                    Monday – Saturday<br />
                    10:00 AM – 7:00 PM IST<br />
                    (Excluding national holidays)
                  </span>
                </p>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="lg:col-span-2">
              <div className="bg-[#F9F9F7] p-6 rounded-sm mb-6">
                <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold text-[#0F172A] mb-2">Submit a Grievance</h2>
                <p className="text-muted-foreground text-sm">
                  Please provide detailed information about your concern so we can assist you better.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter your full name"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Enter your email"
                      className="mt-2"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+91 94650 24516"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">Grievance Category *</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="account">Account Issues</SelectItem>
                        <SelectItem value="payment">Payment / Billing</SelectItem>
                        <SelectItem value="membership">Premium Membership</SelectItem>
                        <SelectItem value="listing">Property Listing</SelectItem>
                        <SelectItem value="fraud">Fraud / Scam Report</SelectItem>
                        <SelectItem value="technical">Technical Issues</SelectItem>
                        <SelectItem value="privacy">Privacy Concerns</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Brief subject of your grievance"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Please provide detailed description of your grievance. Include any relevant transaction IDs, dates, and evidence."
                    rows={6}
                    className="mt-2"
                  />
                </div>

                <div className="bg-gray-100 p-4 rounded-sm text-sm text-muted-foreground">
                  <p>
                    By submitting this form, you confirm that the information provided is accurate and complete. 
                    We may contact you for additional information if required.
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white text-lg py-6"
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit Grievance'}
                </Button>
              </form>
            </div>
          </div>

          {/* Escalation Section */}
          <div className="mt-12 pt-8 border-t">
            <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold text-[#0F172A] mb-4">Escalation Process</h2>
            <p className="text-muted-foreground mb-6">
              If you are not satisfied with the resolution provided, you may escalate your grievance:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-[#F9F9F7] p-6 rounded-sm">
                <div className="text-orange-500 font-bold text-2xl mb-2">Level 1</div>
                <h4 className="font-medium text-[#0F172A] mb-2">Customer Support</h4>
                <p className="text-muted-foreground text-sm">
                  Contact our support team for initial resolution.<br />
                  <strong>Response Time:</strong> 24-48 hours
                </p>
              </div>
              <div className="bg-[#F9F9F7] p-6 rounded-sm">
                <div className="text-orange-500 font-bold text-2xl mb-2">Level 2</div>
                <h4 className="font-medium text-[#0F172A] mb-2">Grievance Officer</h4>
                <p className="text-muted-foreground text-sm">
                  Escalate to our designated Grievance Officer.<br />
                  <strong>Response Time:</strong> 7 days
                </p>
              </div>
              <div className="bg-[#F9F9F7] p-6 rounded-sm">
                <div className="text-orange-500 font-bold text-2xl mb-2">Level 3</div>
                <h4 className="font-medium text-[#0F172A] mb-2">Management Review</h4>
                <p className="text-muted-foreground text-sm">
                  Final review by senior management.<br />
                  <strong>Response Time:</strong> 15 days
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t flex gap-4">
            <Link to="/privacy" className="text-orange-500 hover:underline">Privacy Policy</Link>
            <Link to="/terms" className="text-orange-500 hover:underline">Terms of Service</Link>
            <Link to="/refund-policy" className="text-orange-500 hover:underline">Refund Policy</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrievancePage;
